/**
 * Adaptación al envoltorio nativo.
 *
 * Todo lo de aquí es opcional a propósito: en el navegador los plugins de
 * Capacitor no existen, así que cada uso va detrás de una comprobación y falla
 * en silencio. La alternativa —dos ramas de código, una web y otra móvil— es
 * cómo se acaba con una versión que funciona y otra que nadie prueba.
 */

import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';

/** Corriendo dentro de la app instalada, no en un navegador. */
export const isNative = Capacitor.isNativePlatform();

/**
 * Prepara la ventana nativa y devuelve la función de limpieza.
 *
 * El botón atrás de Android es lo importante: por defecto cierra la app de
 * golpe, y hacerlo en mitad de una partida es perderla. Aquí se intercepta para
 * que solo cierre desde la pantalla principal.
 */
export async function setupNativeShell(onBack: () => boolean): Promise<() => void> {
  if (!isNative) return () => {};

  await Promise.all([
    StatusBar.setStyle({ style: Style.Dark }).catch(() => {}),
    SplashScreen.hide().catch(() => {}),
  ]);

  const listener = await App.addListener('backButton', ({ canGoBack }) => {
    // onBack devuelve true si ha consumido el gesto (cerrar un panel, volver a
    // la pantalla de juego). Solo si nadie lo consume se sale de la app.
    const handled = onBack();
    if (!handled && !canGoBack) void App.exitApp();
  });

  return () => {
    void listener.remove();
  };
}

/**
 * Vibración corta al revelar una carta o cerrar un trato.
 *
 * Silenciosa si el dispositivo no la soporta: un juego que revienta porque el
 * móvil no tiene motor de vibración sería absurdo.
 */
export async function tap(style: 'light' | 'medium' = 'light'): Promise<void> {
  if (!isNative) return;
  try {
    await Haptics.impact({
      style: style === 'light' ? ImpactStyle.Light : ImpactStyle.Medium,
    });
  } catch {
    /* sin motor de vibración */
  }
}
